import { observer } from 'mobx-react-lite';
import { AlertTriangle, Archive, RotateCcw, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import type { Server } from '../../../types';
import { apiFetch } from '../../../utils/api';
import { serverStore } from '../../../stores/server.store';
import { userStore } from '../../../stores/user.store';
import { confirm } from '../../../stores/confirm.store';
import { Button } from '../../design/Button';
import { SettingsSection } from './primitives';
import { can } from '@/utils/permissions';

const Row = ({ title, text, children }: { title: string; text: React.ReactNode; children: React.ReactNode }) => (
  <div className="flex flex-wrap items-center justify-between gap-4">
    <div className="min-w-0">
      <div className="text-sm font-medium text-fg">{title}</div>
      <div className="text-xs text-fg-muted mt-0.5 leading-relaxed">{text}</div>
    </div>
    {children}
  </div>
);

/** Archivieren / Wiederherstellen / Löschen – nur für Admins, außerhalb des Formulars. */
export const DangerZone = observer(({ server }: { server: Server }) => {
  const cu = userStore.currentUser;
  if (!can(cu, 'can_delete_servers')) return null;

  const archived = server.status === 'archived' || server.status === 'restoring_archive';
  const progress = serverStore.archiveProgress.get(server.id)?.percent || 0;

  const restore = async () => {
    const ok = await confirm({
      title: 'Server wiederherstellen?',
      message: 'Der Server wird von der HDD (/mnt/games/archive) zurück auf die NVMe-SSD geschoben. Das kann je nach Größe einige Minuten dauern.',
      confirmText: 'Wiederherstellen',
      variant: 'info',
      icon: 'refresh',
    });
    if (!ok) return;
    const id = toast.loading('Stelle Server wieder her …');
    const res = await apiFetch(`/api/servers/${server.id}/restore-archive`, { method: 'POST' });
    if (res.ok) {
      toast.success('Server wiederhergestellt.', { id });
      serverStore.fetchData();
    } else {
      toast.error('Wiederherstellung fehlgeschlagen.', { id });
    }
  };

  const archive = async () => {
    const ok = await confirm({
      title: 'Server archivieren?',
      message: `Möchtest du "${server.name}" wirklich archivieren? Er wird auf die HDD verschoben und taucht erst nach dem Wiederherstellen wieder auf.`,
      confirmText: 'Archivieren',
      variant: 'danger',
      icon: 'trash',
    });
    if (!ok) return;
    const res = await apiFetch(`/api/servers/${server.id}/archive`, { method: 'POST' });
    if (res.ok) {
      toast.success('Archivierung gestartet.');
      serverStore.fetchData();
    } else {
      toast.error('Archivieren konnte nicht starten.');
    }
  };

  if (archived) {
    return (
      <SettingsSection id="set-gefahr" tone="slate" icon={<Archive size={16} />} title="Archiv" description="Der Server liegt auf der HDD und ist bis zum Wiederherstellen nicht startbar.">
        <Row
          title="Server wiederherstellen"
          text={server.status === 'restoring_archive'
            ? <span className="text-warn animate-pulse">Wiederherstellung läuft …</span>
            : 'Verschiebt den Server zurück auf die Hauptfestplatte (NVMe).'}
        >
          <Button size="sm" disabled={server.status === 'restoring_archive'} onClick={restore}>
            <RotateCcw size={14} /> Wiederherstellen
          </Button>
        </Row>
      </SettingsSection>
    );
  }

  return (
    <SettingsSection id="set-gefahr" danger icon={<AlertTriangle size={16} />} title="Gefahrenzone" description="Dinge, die sich nicht so leicht rückgängig machen lassen.">
      <Row
        title="Server archivieren"
        text={server.status === 'archiving'
          ? `Archivierung läuft … ${progress}%`
          : server.status !== 'offline'
            ? <span className="text-red-400">Erst stoppen – nur ein gestoppter Server kann archiviert werden.</span>
            : 'Verschiebt den Server auf die zweite Festplatte (/mnt/games/archive) und macht Platz auf der SSD.'}
      >
        <Button variant="danger" size="sm" disabled={server.status !== 'offline'} onClick={archive}>
          <Archive size={14} /> Archivieren
        </Button>
      </Row>
      {server.status === 'archiving' && (
        <div className="w-full bg-red-900/20 h-1.5 rounded-full overflow-hidden">
          <div className="bg-red-500 h-full transition-all duration-300 animate-pulse" style={{ width: `${progress}%` }} />
        </div>
      )}
      <div className="border-t border-red-500/15" />
      <Row
        title="Server löschen"
        text={<>Entfernt <code className="bg-black/30 px-1 py-0.5 rounded text-red-300 text-[10px]">installs/{server.id}</code> unwiderruflich von der Festplatte.</>}
      >
        <Button variant="danger" size="sm" onClick={() => { serverStore.selectedServerId = server.id; serverStore.showDeleteModal = true; }}>
          <Trash2 size={14} /> Löschen
        </Button>
      </Row>
    </SettingsSection>
  );
});
